/**
 * Connectors — end-user Connector Catalog (E35).
 *
 * Each card is one virtual_server (auto-materialised per registered
 * backend). Stanford end-users land here, browse what's available
 * to their workgroups, click "Add to Claude" on the ones they want.
 * The modal hands them the per-domain URL to paste into Claude.ai's
 * Connectors panel — the only piece they can't do server-side.
 *
 * Why this exists: with one umbrella `/mcp` connector, Claude.ai's
 * multi-MCP router loses domain-named queries ("ServiceNow incidents")
 * to Anthropic's built-in `mcp-registry`. Per-domain connectors win
 * the routing battle — same pattern Composio uses for 500+
 * integrations. See docs/enhancements/E35.
 */
import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, Copy, ExternalLink, Globe, PlugZap, Search, ShieldCheck } from 'lucide-react';

import { useVirtualServersPaged, type VirtualServerRow } from '@/api/index.js';
import { Button } from '@/components/ui/button.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.js';
import { Input } from '@/components/ui/input.js';
import { PageHeader } from '@/components/console/PageHeader.js';
import { SectionCard } from '@/components/console/SectionCard.js';
import { AsyncShell } from '@/components/console/StateBlocks.js';

interface CardData {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly description: string;
  readonly endpointUrl: string;
  readonly toolCount: number;
  /**
   * Where the connector's data lives, which is the only distinction a
   * user actually needs. `stanford` = we build and host it, and the
   * delegation JWT never leaves our control (native_mcp / rest_wrapper /
   * managed_stdio). `federated` = third-party SaaS reached through
   * Composio, where the user's provider login sits in Composio's vault.
   *
   * Matching on "not stanford" rather than an explicit federated list is
   * deliberate: E39 introduced `composio_router` and the old check tested
   * only for `composio_mcp`, so the router silently classified itself as
   * Stanford-hosted — exactly the wrong way for a safety label to fail.
   */
  readonly kind: 'stanford' | 'federated';
}

/**
 * Where the connector's data lives. Shown on every card because it is the
 * one thing a user should know before pointing an AI at it: a federated
 * connector means their login for that app is held by Composio, not by
 * Stanford.
 */
function KindBadge({ kind }: { kind: CardData['kind'] }): JSX.Element {
  const federated = kind === 'federated';
  const Icon = federated ? Globe : ShieldCheck;
  return (
    <span
      title={
        federated
          ? 'Third-party SaaS reached through Composio. Your login for each app is stored in Composio’s vault, not at Stanford.'
          : 'Built and hosted by Stanford. Data stays inside Stanford systems.'
      }
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 11,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        color: federated ? '#92400e' : '#15803d',
        background: federated ? '#fef3c7' : '#dcfce7',
        padding: '2px 8px',
        borderRadius: 999,
      }}
    >
      <Icon size={11} aria-hidden />
      {federated ? 'Federated SaaS' : 'Stanford-hosted'}
    </span>
  );
}

function rowToCard(row: VirtualServerRow): CardData {
  // composition is jsonb — list of {tool_id, exposure, policy}. Count
  // 'included' entries for the card's "N tools" line.
  const comp = Array.isArray(row.composition)
    ? (row.composition as readonly { exposure?: string }[])
    : [];
  const toolCount = comp.filter((c) => c?.exposure === 'included').length;
  const kind: CardData['kind'] = row.backend_type?.startsWith('composio')
    ? 'federated'
    : 'stanford';
  return {
    id: row.id,
    slug: row.slug,
    name: row.name,
    description: row.description ?? '',
    endpointUrl: row.endpointUrl,
    toolCount,
    kind,
  };
}

export default function Connectors(): JSX.Element {
  const { data, isLoading, isError, error } = useVirtualServersPaged({ status: 'active' });
  const [query, setQuery] = useState('');
  const [opened, setOpened] = useState<CardData | null>(null);

  // E36/E37 deep-link: `/connectors?connect=<slug>` lands here from the
  // proxy's reconnect-needed error message. We filter the visible cards
  // to just that slug, switch to the appropriate tab so it's the only
  // card showing, and pulse the card so the user can't miss it. Cleared
  // out of the URL on first render so a refresh doesn't keep filtering.
  const [searchParams, setSearchParams] = useSearchParams();
  const focusSlug = searchParams.get('connect');
  const focusRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!focusSlug) return;
    // Scroll the focused card into view once it renders.
    const tid = window.setTimeout(() => {
      focusRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
    return () => window.clearTimeout(tid);
  }, [focusSlug]);

  const allCards = useMemo<CardData[]>(() => (data?.items ?? []).map(rowToCard), [data?.items]);

  const cards = useMemo<CardData[]>(() => {
    // Deep-link overrides tab + query: when `?connect=<slug>` is set we
    // show only that one card regardless of what the user picked.
    if (focusSlug) {
      const match = allCards.filter((c) => c.slug === focusSlug);
      if (match.length > 0) return match;
    }
    // No tab filter. The strip made sense at 38 cards (E36's 35 Composio
    // connectors + 3 private); after E39 collapsed those into a single
    // federated connector there are ~4 cards total, and filtering four
    // items costs more attention than it saves. The stanford/federated
    // distinction now rides on a per-card badge, visible without a click.
    const q = query.trim().toLowerCase();
    if (!q) return allCards;
    return allCards.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.slug.toLowerCase().includes(q) ||
        c.description.toLowerCase().includes(q),
    );
  }, [allCards, query, focusSlug]);

  function clearFocus(): void {
    const next = new URLSearchParams(searchParams);
    next.delete('connect');
    setSearchParams(next, { replace: true });
  }

  return (
    <>
      <PageHeader
        eyebrow="Discover"
        title="Connector Catalog"
        description="One MCP connector per domain. Add the ones you need to Claude.ai — each connector is independent, so you can revoke or update them individually."
      />

      <SectionCard
        icon={PlugZap}
        title={`Available connectors (${cards.length})`}
        text="Active connectors your workgroups can access. Click any card to copy its URL and setup steps. The badge on each card tells you whether it is hosted by Stanford or reaches a third-party SaaS through Composio."
      >
        {focusSlug && (
          <div
            style={{
              marginBottom: 12,
              padding: '8px 12px',
              borderRadius: 6,
              background: '#fef3c7',
              border: '1px solid #fbbf24',
              fontSize: 13,
              color: '#78350f',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span>
              Showing the <strong>{focusSlug}</strong> connector card (click{' '}
              <strong>Connect</strong> below). Linked here because a Claude.ai tool call reported a
              stale or missing connection.
            </span>
            <button
              type="button"
              onClick={clearFocus}
              style={{
                background: 'transparent',
                border: '1px solid #d97706',
                color: '#78350f',
                padding: '2px 10px',
                borderRadius: 999,
                fontSize: 12,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              Show all
            </button>
          </div>
        )}

        <div style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
          <Search size={16} aria-hidden style={{ flexShrink: 0, color: 'var(--muted)' }} />
          <Input
            placeholder="Search by name, domain, or description (e.g. 'incidents', 'expenses', 'emails')"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ maxWidth: 520 }}
          />
        </div>

        <AsyncShell
          isLoading={isLoading}
          isError={isError}
          isEmpty={cards.length === 0}
          error={error}
          loadingTitle="Loading connectors"
          errorTitle="Connector catalog unreachable"
          emptyTitle={query ? 'No matches for that search' : 'No active connectors yet'}
          emptyHint={
            query ? (
              <>Try a different keyword, or clear the search to see all available connectors.</>
            ) : (
              <>
                An admin needs to register at least one MCP server. Once registered, it appears here
                automatically — no extra setup. See the admin Build → Servers page.
              </>
            )
          }
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: 16,
            }}
          >
            {cards.map((card) => (
              <ConnectorCard
                key={card.id}
                card={card}
                onAdd={() => setOpened(card)}
                focused={card.slug === focusSlug}
                focusRef={card.slug === focusSlug ? focusRef : undefined}
              />
            ))}
          </div>
        </AsyncShell>
      </SectionCard>

      <AddToClaudeDialog
        card={opened}
        open={opened !== null}
        onOpenChange={(o) => !o && setOpened(null)}
      />
    </>
  );
}

function ConnectorCard({
  card,
  onAdd,
  focused,
  focusRef,
}: {
  card: CardData;
  onAdd: () => void;
  focused?: boolean;
  focusRef?: React.RefObject<HTMLDivElement | null>;
}): JSX.Element {
  // E39 — no per-app Connect button any more. E36 gave every Composio
  // toolkit its own backend, so a card could own a per-user OAuth flow.
  // The router replaced those with one connector spanning every approved
  // app, and connecting is now per-app and in-conversation: a tool call
  // that needs authorisation returns a COMPOSIO_MANAGE_CONNECTIONS
  // instruction with an authorize link. There is nothing meaningful for
  // this page to connect, so the button (and its hooks) are gone.

  return (
    <article
      ref={focusRef}
      style={{
        border: focused ? '2px solid #f59e0b' : '1px solid var(--border)',
        borderRadius: 8,
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        background: 'var(--card-bg, white)',
        boxShadow: focused ? '0 0 0 4px rgba(245, 158, 11, 0.18)' : undefined,
        transition: 'box-shadow 200ms ease',
      }}
    >
      <header style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <PlugZap size={18} aria-hidden style={{ color: 'var(--accent)' }} />
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, flex: 1 }}>{card.name}</h3>
        <KindBadge kind={card.kind} />
      </header>
      <code
        style={{
          fontSize: 12,
          color: '#475569',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        }}
      >
        {card.slug}
      </code>
      {card.description ? (
        <p
          style={{
            margin: '4px 0 0 0',
            fontSize: 13.5,
            color: '#1f2937',
            lineHeight: 1.5,
            display: '-webkit-box',
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {card.description}
        </p>
      ) : (
        <p
          style={{
            margin: '4px 0 0 0',
            fontSize: 13.5,
            fontStyle: 'italic',
            color: '#6b7280',
          }}
        >
          No description provided.
        </p>
      )}
      <div
        style={{
          marginTop: 'auto',
          paddingTop: 12,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span style={{ fontSize: 12, color: '#475569', fontWeight: 500 }}>
          {card.toolCount} {card.toolCount === 1 ? 'tool' : 'tools'}
        </span>
        <Button size="sm" onClick={onAdd}>
          Add to Claude
        </Button>
      </div>
    </article>
  );
}

function AddToClaudeDialog({
  card,
  open,
  onOpenChange,
}: {
  card: CardData | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  if (!card)
    return (
      <Dialog open={false} onOpenChange={onOpenChange}>
        <div />
      </Dialog>
    );

  async function copyUrl(): Promise<void> {
    if (!card) return;
    try {
      await navigator.clipboard.writeText(card.endpointUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* fallback: prompt the user to copy manually */
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Add &ldquo;{card.name}&rdquo; to Claude</DialogTitle>
          <DialogDescription>
            Paste this URL into Claude.ai&apos;s Connectors panel. The connector authenticates via
            your Stanford Google account on first use.
          </DialogDescription>
        </DialogHeader>
        <ol
          style={{
            margin: '16px 0',
            paddingLeft: 24,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          <li>
            <div style={{ fontSize: 13, lineHeight: 1.5 }}>
              Open <strong>Claude.ai</strong> → <strong>Settings</strong> →{' '}
              <strong>Connectors</strong> → <strong>Add custom MCP connector</strong>.
            </div>
          </li>
          <li>
            <div style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 8 }}>Paste this URL:</div>
            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'stretch',
                background: 'var(--code-bg, #f4f4f5)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                padding: 8,
              }}
            >
              <code
                style={{
                  flex: 1,
                  fontSize: 12,
                  wordBreak: 'break-all',
                  alignSelf: 'center',
                  fontFamily: 'monospace',
                }}
              >
                {card.endpointUrl}
              </code>
              <Button size="sm" variant="outline" onClick={copyUrl}>
                {copied ? (
                  <>
                    <Check size={14} aria-hidden /> Copied
                  </>
                ) : (
                  <>
                    <Copy size={14} aria-hidden /> Copy
                  </>
                )}
              </Button>
            </div>
          </li>
          <li>
            <div style={{ fontSize: 13, lineHeight: 1.5 }}>
              Suggested name in Claude: <strong>{card.name}</strong>
            </div>
          </li>
          <li>
            <div style={{ fontSize: 13, lineHeight: 1.5 }}>
              When Claude prompts for sign-in, choose your <strong>@stanford.edu</strong> Google
              account.
            </div>
          </li>
        </ol>
        <DialogFooter className="flex flex-row gap-2 sm:justify-between">
          <Button variant="ghost" asChild>
            <a href="https://claude.ai/settings/connectors" target="_blank" rel="noreferrer">
              Open Claude.ai <ExternalLink size={14} aria-hidden />
            </a>
          </Button>
          <Button onClick={() => onOpenChange(false)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
